import Typography from '@mui/material/Typography';
import { isSameDay } from 'date-fns';
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BusinessChart } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { DatepickerSelect } from 'shared/components/ui/datepicker-select';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { SliderButtons } from 'shared/components/ui/slider-buttons';
import { useToday } from 'shared/contexts/today';
import { useFormattedNumericInput } from 'shared/hooks/use-formatted-numeric-input';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { DateFormatOptions } from 'shared/types/date-format-options';
import {
  MetricEntry,
  MetricEntryMode,
  MetricUnit,
} from 'shared/types/goal-metric';
import { GoalProgressHistoryFormFields } from 'shared/types/goal-progress-form';
import { metricUnitFormatBase } from 'shared/types/metric-template';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { calculateMetricEntryValue } from 'shared/utils/calculate-metric-entry-value';
import { getLocalizedStringValue } from 'shared/utils/get-localized-string-value';
import { isNumber } from 'shared/utils/is-number';
import { useTheme } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import * as Styled from './goal-progress-add-entry-form.style';

enum SymbolLocations {
  Before = 'before',
  After = 'after',
}

const createInitialEntry = (
  date: Timestamp,
  mode = MetricEntryMode.Absolute,
) => ({
  id: uuidv4(),
  date,
  value: undefined as unknown as number,
  mode,
});

export type GoalProgressAddEntryFormProps = {
  entries: GoalProgressHistoryFormFields['entries'];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  unit?: MetricUnit;
  startValue?: number;
  onSubmit: (entries: GoalProgressHistoryFormFields['entries']) => void;
  onClose: () => void;
  onOverview?: () => void;
};

export const GoalProgressAddEntryForm: React.FC<
  GoalProgressAddEntryFormProps
> = ({
  entries,
  dateFormat,
  weekStartsOn,
  unit,
  startValue,
  onSubmit,
  onClose,
  onOverview,
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const theme = useTheme();
  const today = useToday();
  const localizedSymbol = unit?.symbol
    ? getLocalizedStringValue(unit.symbol, locale)
    : '';

  const localizedFormat = unit?.format
    ? getLocalizedStringValue(unit.format, locale)
    : undefined;

  const unitSymbolLocation = useMemo(() => {
    if (!localizedFormat) {
      return SymbolLocations.After;
    }

    const valueIndex = localizedFormat.indexOf(metricUnitFormatBase);

    return valueIndex === 0 ? SymbolLocations.After : SymbolLocations.Before;
  }, [localizedFormat]);

  const symbolHasSpace = useMemo(() => {
    if (!localizedFormat) {
      return true;
    }

    const valueIndex = localizedFormat.indexOf(metricUnitFormatBase);

    if (unitSymbolLocation === SymbolLocations.Before) {
      return localizedFormat.charAt(valueIndex - 1) === ' ';
    }

    return (
      localizedFormat.charAt(valueIndex + metricUnitFormatBase.length) === ' '
    );
  }, [localizedFormat, unitSymbolLocation]);

  const lastEntry = useMemo(
    () =>
      entries.toSorted(
        (entryA, entryB) => entryB.date.getTime() - entryA.date.getTime(),
      )[0],
    [entries],
  );

  const [newEntry, setNewEntry] = useState<MetricEntry>(
    createInitialEntry(today, lastEntry?.mode),
  );

  // --- Locale Formatting Logic ---
  const separators = useNumberFormat();
  const { formattedValue, onInputChange, onInputBlur, onInputPaste } =
    useFormattedNumericInput({
      value: newEntry.value,
      onChange: (value) => setNewEntry((oldEntry) => ({ ...oldEntry, value })),
    });

  const placeholderValue = useMemo(() => {
    const format = (value: number) => {
      const stringValue = value.toFixed(2);
      const [integerPart, decimalPart] = stringValue.split('.');

      const formattedIntegerPart = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        separators.group,
      );

      return decimalPart
        ? `${formattedIntegerPart}${separators.decimal}${decimalPart}`
        : formattedIntegerPart;
    };

    if (!newEntry.date) {
      return format(startValue ?? 0);
    }
    const sorted = entries.toSorted(
      (a, b) => b.date.getTime() - a.date.getTime(),
    );
    const lastRelevantEntry = sorted.find(
      (entry) =>
        isSameDay(entry.date, newEntry.date) ||
        entry.date.getTime() < newEntry.date.getTime(),
    );
    if (lastRelevantEntry) {
      return format(
        calculateMetricEntryValue(
          lastRelevantEntry.id,
          entries,
          startValue ?? 0,
        ),
      );
    }
    return format(startValue ?? 0);
  }, [entries, newEntry.date, separators, startValue]);

  const updateSelectedMode = (mode: MetricEntryMode) =>
    setNewEntry((oldEntry) => createInitialEntry(oldEntry.date, mode));

  const sliderButtons = useMemo(
    () => [
      [
        {
          label: t('forms.goal-progress.add-entry.tabs.absolute.title'),
          value: MetricEntryMode.Absolute,
        },
        {
          label: t('forms.goal-progress.add-entry.tabs.increment.title'),
          value: MetricEntryMode.Relative,
        },
      ],
    ],
    [t],
  );

  const submitForm = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();

      if (!isNumber(newEntry.value) || !newEntry.date) {
        onClose();
        return false;
      }
      // set the time of the new entry to the time of the submission
      const time = new Date();
      const date = newEntry.date;
      date.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
      newEntry.date = date;

      onSubmit([newEntry, ...entries]);
      onClose();

      return false;
    },
    [entries, newEntry, onClose, onSubmit],
  );

  const updateEntryDate = async (date?: Timestamp) => {
    if (!date) {
      return;
    }

    setNewEntry((oldEntry) => ({ ...oldEntry, date }));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return;
    }

    const activeElement = document.activeElement as HTMLElement;
    if (!activeElement) {
      // without focus on something the normal flow should continue
      return;
    }

    const elementName = activeElement.getAttribute('name') ?? '';

    if (elementName.startsWith('entries.')) {
      event.preventDefault();
      event.stopPropagation();
      // when it's one of the entries, blurring it will trigger the functionality needed.
      activeElement.blur();
      return;
    }
  };

  const valueInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (valueInputRef.current) {
      valueInputRef.current.focus();
    }
  }, []); // Focus on mount

  useEffect(() => {
    if (valueInputRef.current) {
      valueInputRef.current.focus();
    }
  }, [newEntry.mode]); // Focus on tab switch

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') {
        return;
      }
      const elementName = document.activeElement?.getAttribute('name') ?? '';

      if (elementName.startsWith('entry.')) {
        return;
      }

      e.preventDefault();
      submitForm();
    };

    window.addEventListener('keydown', onEnter);

    return () => window.removeEventListener('keydown', onEnter);
  }, [submitForm]);

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {t('forms.goal-progress.add-entry.title')}
        </Typography>
      </Styled.Header>

      <Styled.Body>
        <SliderButtons
          optionGroups={sliderButtons}
          onOption={updateSelectedMode}
          value={newEntry.mode}
          isFullWidth
          backgroundColor={theme.palette.grey[400]}
        />

        <Styled.Section>
          <Styled.SectionItem $isDate>
            <DatepickerSelect
              value={newEntry.date}
              onChange={updateEntryDate}
              dateFormat={dateFormat}
              weekStartsOn={weekStartsOn}
              placeholder={t('general.labels.dates.select')}
            />
          </Styled.SectionItem>

          <Styled.SectionItem>
            {newEntry.mode === MetricEntryMode.Relative && (
              <Typography variant="subtitle1" component="span">
                <Styled.Affix>
                  {unitSymbolLocation === SymbolLocations.Before &&
                    `${localizedSymbol}${symbolHasSpace ? ' ' : ''}`}
                </Styled.Affix>
                {placeholderValue}
                <Styled.Affix>
                  {unitSymbolLocation === SymbolLocations.After &&
                    `${symbolHasSpace ? ' ' : ''}${localizedSymbol}`}
                  {` + `}
                </Styled.Affix>
              </Typography>
            )}

            {newEntry.mode === MetricEntryMode.Absolute &&
              unitSymbolLocation === SymbolLocations.Before && (
                <Styled.Affix>{`${localizedSymbol}${symbolHasSpace ? ' ' : ''}`}</Styled.Affix>
              )}

            <ResizableInput
              ref={valueInputRef}
              value={formattedValue}
              placeholder={
                newEntry.mode === MetricEntryMode.Absolute
                  ? placeholderValue
                  : `0${separators.decimal}00`
              }
              inputMode="decimal"
              type="text"
              onKeyDown={onKeyDown}
              onChange={onInputChange}
              onBlur={onInputBlur}
              onPaste={onInputPaste}
              minWidth="0"
              inheritFont
              hideNumberButtons
            />
            {newEntry.mode === MetricEntryMode.Absolute &&
              unitSymbolLocation === SymbolLocations.After && (
                <Styled.Affix>{`${symbolHasSpace ? ' ' : ''}${localizedSymbol}`}</Styled.Affix>
              )}
          </Styled.SectionItem>
        </Styled.Section>
      </Styled.Body>

      <Styled.Footer>
        {!!onOverview && (
          <Button
            variant={Variants.Text}
            size={Sizes.Medium}
            onClick={onOverview}
            startIcon={BusinessChart}
            color={Colors.Inherit}
            type="button"
          >
            {t('forms.goal-progress.add-entry.buttons.overview.label')}
          </Button>
        )}

        <Styled.Interactions>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.White}
            onClick={onClose}
            type="button"
          >
            {t('forms.goal-progress.add-entry.buttons.cancel.label')}
          </Button>
          <Button
            size={Sizes.Medium}
            type="submit"
            disabled={!isNumber(newEntry.value) || !newEntry.date}
          >
            {t('forms.goal-progress.add-entry.buttons.save.label')}
          </Button>
        </Styled.Interactions>
      </Styled.Footer>
    </Styled.Form>
  );
};
